import dayjs from 'dayjs';
import { compact } from 'lodash';

export const formatName = (firstName?: string, lastName?: string): string =>
  compact([firstName, lastName]).join(' ');

export const formatNumber = (value?: number | string, fullNumber?: boolean): string => {
  if (!value) {
    return '0';
  }

  if (typeof value === 'string' && isNaN(parseInt(value))) {
    return value;
  }

  if (fullNumber) {
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  if (Number(value) < 1000) {
    return value.toString();
  }

  const suffixes = ['', 'K', 'M', 'B', 'T'];
  const tier = Math.floor(Math.log10(Number(value)) / 3);

  const scaled = Number(value) / Math.pow(10, tier * 3);
  const formatted = scaled.toFixed(scaled % 1 === 0 ? 0 : 1);

  return formatted + suffixes[tier];
};

export const formatAndRound = (value?: number): string => {
  if (!value) {
    return '0';
  }

  const roundedValue = Math.round(value);

  return roundedValue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export const calculatePercentage = (part: number, whole: number) => (part / whole) * 100;

export const formatTime = (seconds: number) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
};

export const formatDuration = (seconds: number): string => {
  const units = [
    { label: 'day', secs: 86400 },
    { label: 'hour', secs: 3600 },
    { label: 'minute', secs: 60 },
    { label: 'second', secs: 1 },
  ];

  const result: string[] = [];

  for (const unit of units) {
    const count = Math.floor(seconds / unit.secs);
    if (count > 0) {
      result.push(`${count} ${unit.label}${count !== 1 ? 's' : ''}`);
      seconds %= unit.secs;
    }
  }

  return result.length > 0 ? result.join(', ') : '0 seconds';
};

export const getProgressPercentage = (start: string | Date, end: string | Date): number => {
  const startDate = dayjs(start);
  const endDate = dayjs(end);
  const now = dayjs();

  const total = endDate.diff(startDate);
  const elapsed = now.diff(startDate);

  if (total <= 0) return 100;
  return Math.min(Math.max((elapsed / total) * 100, 0), 100);
};
